/**
 * RmsPatientRemoveForm
 * --------------------
 * Confirmation dialog for archiving (soft-deleting) an RMS patient referral.
 *
 * Modelled on the ADHD PatientRemoveForm but adapted for the RMS data model:
 *   - Calls `rmsArchivePatientRecord` instead of `removePatient`
 *   - Passes `orgId` + `patientRecordId` instead of `patientId`
 *   - Copy references "referral" rather than "patient" where appropriate
 *
 * The dialog uses a "type DELETE to confirm" safety mechanism to prevent
 * accidental removals. While the operation is in-flight, the dialog cannot
 * be dismissed and the cancel button is disabled.
 *
 * On success, the caller is notified via `onSuccess(patientRecordId)` so
 * the parent can optimistically remove the row from state.
 */

import React, { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon } from '@heroicons/react/solid';
import { auth, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

function RmsPatientRemoveForm({ isOpen, onClose, patient, orgId, onSuccess }) {
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Block dismissal while the backend operation is running so the user
  // cannot accidentally close the dialog and lose feedback.
  const handleClose = React.useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  React.useEffect(() => {
    if (isOpen) {
      setDeleteText('');
      setIsDeleting(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  // Derive a display name from the patient record. The subject may be stored
  // either under `subject.firstName/lastName` or as top-level legacy fields.
  const patientName = (() => {
    const subj = patient?.subject || {};
    const first = subj.firstName || patient?.firstName || '';
    const last = subj.lastName || patient?.lastName || '';
    return `${first} ${last}`.trim() || 'Unknown';
  })();

  const handleArchive = async () => {
    if (deleteText !== 'DELETE' || !patient) return;

    setIsDeleting(true);
    setError(null);

    try {
      const archiveFn = httpsCallable(functions, 'rmsArchivePatientRecord');
      await archiveFn({
        orgId,
        patientRecordId: patient?.id,
        reason: `${auth.currentUser?.displayName || 'Provider'} requested deletion via RMS dashboard`,
      });

      toast.success(`Referral for ${patientName} has been removed`);
      setSuccess(true);

      // Notify the parent so it can optimistically remove the row
      if (onSuccess) {
        try { onSuccess(patient?.id); } catch (_) {}
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('[RmsPatientRemoveForm] Archive failed:', err);
      setError(err.message || 'An error occurred while trying to remove the referral.');
    } finally {
      setIsDeleting(false);
    }
  };

  const isDeleteEnabled = deleteText === 'DELETE' && !isDeleting;

  return (
    <AlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] max-h-[85vh] overflow-auto">
          <AlertDialog.Title className="text-xl font-semibold text-gray-900 flex items-center">
            <ExclamationIcon className="w-6 h-6 text-red-500 mr-2" />
            Remove Referral
          </AlertDialog.Title>

          <div className="mt-3 text-sm text-gray-700">
            <AlertDialog.Description asChild>
              <p className="mb-2">
                You are about to permanently remove this referral:
              </p>
            </AlertDialog.Description>

            <p className="font-medium text-gray-900 mb-4">
              {patientName}
            </p>

            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">
                This action cannot be undone. The patient&apos;s referral records, form
                submissions, and triage data will be archived and removed from view.
                Any service users (school contacts, guardians) linked only to this
                referral will have their accounts disabled.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-4">
                Type DELETE in all caps to confirm:
              </label>
              <input
                type="text"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Type DELETE here"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
              <p className="text-sm text-green-700">
                Referral successfully removed.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeleting}
              >
                Cancel
              </button>
            </AlertDialog.Cancel>

            <button
              onClick={handleArchive}
              disabled={!isDeleteEnabled}
              className={`px-4 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isDeleteEnabled
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-red-300 cursor-not-allowed'
              }`}
            >
              {isDeleting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Removing...
                </span>
              ) : (
                'Remove Referral'
              )}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default RmsPatientRemoveForm;
